import React from 'react';

import { Pie, ResponsiveContainer, PieChart as PieChartComp, Sector } from 'recharts';

import { statisticsTypeMapping } from 'src/features/statistic/utils/constants';
import { CURRENCY } from 'src/types';
import { useIntl } from 'react-intl';
import { getFillColor } from 'src/features/statistic/utils/utils';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { SectionBlockSubtitle } from 'src/registry/components/blocks/SectionBlock';

type Props = {
    dataKey: string;
    data?: { [key: string]: string | number }[];
    isLoading: boolean;
    currency: CURRENCY;
    children?: React.ReactNode;
    fieldName?: string;
};

type Payload = { [key: string]: string | number };

export default function DynamicColorPieChart({ dataKey, data, isLoading, children, currency, fieldName }: Props) {
    const [index, setIndex] = React.useState<null | number>(null);
    const { formatMessage, formatNumber } = useIntl();

    return (
        <div className="relative flex-1">
            <ResponsiveContainer width="100%" height={250}>
                <PieChartComp onMouseLeave={() => setIndex(null)}>
                    <Pie
                        data={data}
                        dataKey={dataKey}
                        cx="50%"
                        cy="50%"
                        innerRadius="70"
                        outerRadius="100"
                        isAnimationActive
                        onMouseEnter={(_, index) => {
                            setIndex(index);
                        }}
                        onMouseLeave={() => {
                            setIndex(null);
                        }}
                        shape={(props) => {
                            const payload = props.payload as Payload;

                            return (
                                <Sector
                                    {...props}
                                    className="hover:opacity-75"
                                    fill={fieldName ? getFillColor(String(payload[fieldName]), props.index) : undefined}
                                />
                            );
                        }}
                    />
                </PieChartComp>
            </ResponsiveContainer>
            <div className="absolute inset-x-0 top-25 z-0">
                <div className="flex flex-col items-center gap-2">
                    {isLoading ? (
                        <Skeleton className="h-8 w-27" />
                    ) : (
                        <p className="text-sm">
                            <span className="text-2xl font-semibold">
                                {index !== null && data && data[index]?.total_amount
                                    ? formatNumber((data[index]?.total_amount as number) / 100, {
                                          style: 'currency',
                                          currency,
                                      })
                                    : null}
                            </span>
                        </p>
                    )}
                    <SectionBlockSubtitle className="text-sm font-medium">
                        {data &&
                            index !== null &&
                            fieldName &&
                            (statisticsTypeMapping[data[index]?.[fieldName]]
                                ? formatMessage(statisticsTypeMapping[data[index]?.[fieldName]])
                                : String(data[index]?.[fieldName] ?? ''))}
                    </SectionBlockSubtitle>
                </div>
            </div>

            {children && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">{children}</div>}
        </div>
    );
}
